import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Settings2, BarChart3, Brain } from 'lucide-react';
import { UserPreferences } from '@/hooks/useUserPreferences';
import { useConsentTracking } from '@/hooks/useConsentTracking';
import { DOCUMENT_VERSIONS } from '@/lib/constants/documentVersions';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface ProcessingObjectionsSectionProps {
  preferences: UserPreferences | null;
  saving: boolean;
  onUpdate: (key: keyof UserPreferences, value: boolean) => Promise<boolean>;
}

// Map preference key + new value to the audit consent_type. We record both
// raising AND withdrawing the objection so the audit trail (GDPR Art. 7(1)
// and Art. 21(1)) reflects the actual moment of the decision.
const consentTypeFor = (key: keyof UserPreferences, newValue: boolean): string | null => {
  if (key === 'object_to_ai_processing') {
    return newValue ? 'ai_processing_objection' : 'ai_processing_objection_withdrawn';
  }
  if (key === 'object_to_analytics') {
    return newValue ? 'analytics_objection' : 'analytics_objection_withdrawn';
  }
  return null;
};

export const ProcessingObjectionsSection: React.FC<ProcessingObjectionsSectionProps> = ({
  preferences,
  saving,
  onUpdate,
}) => {
  const { logConsent } = useConsentTracking();

  const handleToggle = async (key: keyof UserPreferences, newValue: boolean, description: string) => {
    const success = await onUpdate(key, newValue);
    if (!success) return;

    // Record the objection event (or its withdrawal) with server-side IP
    // capture for GDPR Art. 7(1) / Art. 21(1) demonstrability. Best-effort:
    // failures are queued in localStorage by useConsentTracking.
    const consentType = consentTypeFor(key, newValue);
    if (consentType) {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await logConsent(
            { consentType, documentVersion: DOCUMENT_VERSIONS.privacyPolicy },
            user.id,
          );
        }
      } catch (e) {
        console.warn('[GDPR] Failed to log objection event:', e);
      }
    }

    toast.success(newValue ? `${description} disabled` : `${description} enabled`);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings2 className="h-5 w-5" />
          Processing Preferences
        </CardTitle>
        <CardDescription>
          Control how your data is processed (GDPR Art. 21 — Right to Object)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Analytics */}
        <div className="flex items-start justify-between gap-4 p-4 border rounded-lg">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/20">
              <BarChart3 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="object-analytics" className="text-base font-medium">
                Analytics
              </Label>
              <p className="text-sm text-muted-foreground">
                Track usage patterns to improve the platform
              </p>
              {preferences?.object_to_analytics && (
                <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                  Analytics is currently disabled
                </p>
              )}
            </div>
          </div>
          <Switch
            id="object-analytics"
            checked={!(preferences?.object_to_analytics ?? false)}
            onCheckedChange={(checked) => handleToggle('object_to_analytics', !checked, 'Analytics')}
            disabled={saving || !preferences}
          />
        </div>

        {/* AI Processing */}
        <div className="flex items-start justify-between gap-4 p-4 border rounded-lg">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-full bg-purple-100 dark:bg-purple-900/20">
              <Brain className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="object-ai" className="text-base font-medium">
                AI Processing of My Papers
              </Label>
              <p className="text-sm text-muted-foreground">
                Allow AI-generated reviews, metadata extraction and revision
                hints (Vertex AI, EU/EEA)
              </p>
              {preferences?.object_to_ai_processing && (
                <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                  AI processing is currently disabled — new AI reviews,
                  extractions and revision hints are skipped automatically,
                  and existing AI reviews on your papers are hidden from the
                  public site until you re-enable this (GDPR Art. 21(3)).
                </p>
              )}
            </div>
          </div>
          <Switch
            id="object-ai"
            checked={!(preferences?.object_to_ai_processing ?? false)}
            onCheckedChange={(checked) => handleToggle('object_to_ai_processing', !checked, 'AI processing')}
            disabled={saving || !preferences}
          />
        </div>

        <p className="text-xs text-muted-foreground pt-2">
          Essential processing for account management and security cannot be disabled.
          AI review remains opt-in: each generation still requires your explicit consent (GDPR Art. 6(1)(a)).
          Objection events are logged with timestamp and IP address for audit (GDPR Art. 7(1)).
        </p>
      </CardContent>
    </Card>
  );
};
